import React, { useState, useEffect } from 'react';
import { Send, MessageSquare, LifeBuoy, Mail, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import Chatbot from '../components/common/Chatbot';
import api from '../services/api';
import './Support.css';

export default function Support() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Asegurar que siempre se inicie en el tope de la pantalla al entrar
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, []);
  const [formData, setFormData] = useState({
    name: user?.displayName || '',
    email: user?.email || '',
    subject: '',
    message: '',
    category: 'general',
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    try {
      await api.sendSupportMessage({
        ...formData,
        userId: user?.uid || null,
      });

      setSubmitted(true);
    } catch (error) {
      console.error('Support error:', error);
      setErrorMessage(
        error.response?.data?.error ||
        t('support.errorMessage', 'Hubo un problema al enviar tu mensaje. Por favor intenta de nuevo.')
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="support-page">
      <Navbar />

      <main className="support-page__main">
        <div className="support-page__container">
          {/* Columna Izquierda: Información */}
          <div className="support-page__info">
            <h1 className="support-page__title">
              {t('support.titlePre', 'Estamos para')}{' '}
              <span className="text-gradient">{t('support.titleHighlight', 'ayudarte.')}</span>
            </h1>
            <p className="support-page__subtitle">
              {t(
                'support.subtitle',
                '¿Tenés alguna duda, problema técnico o sugerencia? Envianos un mensaje y nuestro equipo te responderá lo antes posible.'
              )}
            </p>

            <div className="support-page__features">
              <div className="support-page__feature-item">
                <div className="support-page__icon-box support-page__icon-box--pink">
                  <MessageSquare className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="support-page__feature-title">
                    {t('support.featureTechTitle', 'Soporte Técnico')}
                  </h3>
                  <p className="support-page__feature-desc">
                    {t(
                      'support.featureTechDesc',
                      'Ayuda con la conexión de TV, búsqueda de YouTube, código QR o interacción móvil.'
                    )}
                  </p>
                </div>
              </div>

              <div className="support-page__feature-item">
                <div className="support-page__icon-box support-page__icon-box--purple">
                  <LifeBuoy className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="support-page__feature-title">
                    {t('support.featureBillingTitle', 'Facturación y Planes')}
                  </h3>
                  <p className="support-page__feature-desc">
                    {t(
                      'support.featureBillingDesc',
                      'Consultas sobre pagos en MercadoPago / Stripe, suscripciones Pro y Premium o cambios de plan.'
                    )}
                  </p>
                </div>
              </div>

              <div className="support-page__feature-item">
                <div className="support-page__icon-box support-page__icon-box--cyan">
                  <Mail className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="support-page__feature-title">
                    {t('support.featureFeedbackTitle', 'Feedback & Sugerencias')}
                  </h3>
                  <p className="support-page__feature-desc">
                    {t(
                      'support.featureFeedbackDesc',
                      'Tus ideas nos ayudan a hacer de Garaoke la mejor fiesta. ¡Queremos saber qué pensás!'
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Columna Derecha: Tarjeta de Formulario Glassmorphic */}
          <div className="support-page__card">
            {submitted ? (
              <div className="support-page__success">
                <div className="support-page__success-icon">
                  <CheckCircle2 className="w-10 h-10" />
                </div>
                <h2 className="support-page__success-title">
                  {t('support.successTitle', '¡Mensaje Recibido!')}
                </h2>
                <p className="support-page__success-desc">
                  {t(
                    'support.successDesc',
                    'Gracias por contactarte con nosotros. Hemos enviado una confirmación a tu correo y te responderemos pronto.'
                  )}
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSubmitted(false);
                    setFormData({
                      name: user?.displayName || '',
                      email: user?.email || '',
                      subject: '',
                      message: '',
                      category: 'general',
                    });
                  }}
                  className="support-page__btn-secondary"
                >
                  {t('support.sendAnother', 'Enviar otro mensaje')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="support-page__form">
                {errorMessage && (
                  <div className="support-page__alert-error">
                    {errorMessage}
                  </div>
                )}

                <div className="support-page__row">
                  <div className="support-page__group">
                    <label className="support-page__label">
                      {t('support.nameLabel', 'Nombre')}
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="support-page__input"
                      placeholder={t('support.namePlaceholder', 'Tu nombre')}
                    />
                  </div>
                  <div className="support-page__group">
                    <label className="support-page__label">
                      {t('support.emailLabel', 'Email')}
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="support-page__input"
                      placeholder="tu@email.com"
                    />
                  </div>
                </div>

                <div className="support-page__group">
                  <label className="support-page__label">
                    {t('support.categoryLabel', 'Categoría')}
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="support-page__select"
                  >
                    <option value="general">{t('support.catGeneral', 'Consulta General')}</option>
                    <option value="technical">{t('support.catTech', 'Problema Técnico')}</option>
                    <option value="billing">{t('support.catBilling', 'Facturación y Pagos')}</option>
                    <option value="feedback">{t('support.catFeedback', 'Sugerencia / Feedback')}</option>
                    <option value="bug">{t('support.catBug', 'Reportar un Error')}</option>
                  </select>
                </div>

                <div className="support-page__group">
                  <label className="support-page__label">
                    {t('support.subjectLabel', 'Asunto')}
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.subject}
                    onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                    className="support-page__input"
                    placeholder={t('support.subjectPlaceholder', '¿En qué podemos ayudarte?')}
                  />
                </div>

                <div className="support-page__group">
                  <label className="support-page__label">
                    {t('support.messageLabel', 'Mensaje')}
                  </label>
                  <textarea
                    required
                    rows={5}
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="support-page__textarea"
                    placeholder={t('support.messagePlaceholder', 'Escribí tu mensaje acá...')}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="support-page__btn-submit"
                >
                  {loading ? (
                    <div className="support-page__spinner" />
                  ) : (
                    <>
                      <span>{t('support.submitBtn', 'Enviar Mensaje')}</span>
                      <Send className="w-4 h-4" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>

      <Footer />
      <Chatbot />
    </div>
  );
}
